/**
 * Escolha das escolas de um veículo, em ordem de preferência (RN-CP-23, D-15).
 *
 * Uma DRE pode ter centenas de escolas, e a lista inteira de caixas de seleção
 * obrigava a rolar até achar a certa. Aqui a escolha é feita por busca — nome,
 * código EOL, tipo de unidade ou endereço, sem exigir acento — e o que foi
 * escolhido fica numa lista ordenada logo abaixo, onde se troca a ordem ou se
 * remove sem precisar desmarcar e marcar de novo.
 *
 * A ordem continua sendo a da escolha: a primeira escolhida é a 1ª opção, e a
 * próxima entra no fim. A busca fica aberta entre uma escolha e outra; com o
 * teto atingido, as demais escolas aparecem indisponíveis, com o motivo.
 */

import { ArrowDown, ArrowUp, Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/ui/base/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/base/popover';
import { cn, normalizarBusca } from '@/nucleo/utilitarios';

import type { EscolaDaInscricao } from './apiDaInscricao';

export interface SeletorDeEscolasProps {
  /** As escolas que a DRE e a modalidade escolhidas permitem. */
  escolas: EscolaDaInscricao[];
  /** Códigos EOL escolhidos, em ordem de preferência. */
  escolhidas: string[];
  /** Teto de escolas do período. */
  maximo: number;
  aoMudar: (escolhidas: string[]) => void;
  /** Id do texto que explica a escolha, para o leitor de tela. */
  idDaDescricao?: string;
}

type Acao = 'subir' | 'descer' | 'remover';

function detalheDaEscola(escola: EscolaDaInscricao): string {
  return [escola.tipoUnidade, `EOL ${escola.codigoEol}`, escola.endereco]
    .filter(Boolean)
    .join(' · ');
}

export function SeletorDeEscolas({
  escolas,
  escolhidas,
  maximo,
  aoMudar,
  idDaDescricao,
}: SeletorDeEscolasProps) {
  const idBase = useId();
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [anuncio, setAnuncio] = useState('');
  const gatilho = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLOListElement>(null);
  const focoPendente = useRef<{ codigo: string; acao: Acao } | null>(null);

  const cheio = escolhidas.length >= maximo;

  const porCodigo = useMemo(
    () => new Map(escolas.map((escola) => [escola.codigoEol, escola])),
    [escolas],
  );

  // O texto de busca de cada escola é montado uma vez por lista, não a cada tecla.
  const indice = useMemo(
    () =>
      escolas.map((escola) => ({
        escola,
        texto: normalizarBusca(
          [escola.nome, escola.codigoEol, escola.tipoUnidade, escola.endereco ?? ''].join(' '),
        ),
      })),
    [escolas],
  );

  const visiveis = useMemo(() => {
    const termos = normalizarBusca(busca).trim().split(/\s+/).filter(Boolean);
    if (termos.length === 0) return escolas;
    return indice
      .filter(({ texto }) => termos.every((termo) => texto.includes(termo)))
      .map(({ escola }) => escola);
  }, [busca, escolas, indice]);

  // Depois de mover uma escola, o foco acompanha a escola movida — o botão
  // usado pode ter sumido (a 1ª não tem "Subir"), então cai no vizinho.
  useEffect(() => {
    const pendente = focoPendente.current;
    if (!pendente || !lista.current) return;
    focoPendente.current = null;
    const item = lista.current.querySelector<HTMLElement>(
      `[data-escola="${CSS.escape(pendente.codigo)}"]`,
    );
    const alvo =
      item?.querySelector<HTMLElement>(`[data-acao="${pendente.acao}"]`) ??
      item?.querySelector<HTMLElement>('[data-acao]');
    alvo?.focus();
  }, [escolhidas]);

  function nomeDe(codigo: string): string {
    return porCodigo.get(codigo)?.nome ?? `EOL ${codigo}`;
  }

  function alternar(codigo: string) {
    const posicao = escolhidas.indexOf(codigo);
    if (posicao >= 0) {
      aoMudar(escolhidas.filter((c) => c !== codigo));
      setAnuncio(`${nomeDe(codigo)} removida das escolas escolhidas.`);
      return;
    }
    if (cheio) return;
    aoMudar([...escolhidas, codigo]);
    setAnuncio(`${nomeDe(codigo)} escolhida como ${escolhidas.length + 1}ª opção.`);
  }

  function mover(codigo: string, deslocamento: -1 | 1) {
    const de = escolhidas.indexOf(codigo);
    const para = de + deslocamento;
    if (de < 0 || para < 0 || para >= escolhidas.length) return;
    const proxima = [...escolhidas];
    [proxima[de], proxima[para]] = [proxima[para]!, proxima[de]!];
    focoPendente.current = { codigo, acao: deslocamento < 0 ? 'subir' : 'descer' };
    aoMudar(proxima);
    setAnuncio(`${nomeDe(codigo)} passou a ser a ${para + 1}ª opção.`);
  }

  function remover(codigo: string) {
    aoMudar(escolhidas.filter((c) => c !== codigo));
    setAnuncio(`${nomeDe(codigo)} removida das escolas escolhidas.`);
    gatilho.current?.focus();
  }

  return (
    <div className="space-y-3">
      <Popover
        open={aberto}
        onOpenChange={(proximo) => {
          setAberto(proximo);
          if (!proximo) setBusca('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            ref={gatilho}
            type="button"
            role="combobox"
            aria-expanded={aberto}
            aria-controls={aberto ? `${idBase}-busca` : undefined}
            aria-label={`Buscar escola — ${escolhidas.length} de ${maximo} escolhida(s)`}
            aria-describedby={idDaDescricao}
            className={cn(
              'flex h-11 w-full items-center gap-2 rounded-xl border border-input bg-background px-3.5 py-2.5 text-left text-sm ring-offset-background transition-colors',
              'hover:bg-accent/30',
              'focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
            )}
          >
            <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {cheio
                ? `Limite de ${maximo} escola(s) atingido`
                : 'Busque por nome, código EOL ou endereço'}
            </span>
            <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
              {escolhidas.length}/{maximo}
            </span>
            <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          id={`${idBase}-busca`}
          align="start"
          className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0"
        >
          <Command shouldFilter={false} label="Buscar escola">
            <CommandInput
              placeholder="Nome, código EOL ou endereço"
              value={busca}
              onValueChange={setBusca}
              aria-describedby={cheio ? `${idBase}-teto` : undefined}
            />
            {cheio && (
              <p
                id={`${idBase}-teto`}
                className="border-b border-border bg-muted/40 px-3 py-2 text-xs text-foreground"
              >
                Você já escolheu {maximo} escola(s). Para trocar, remova uma da lista ou desmarque-a
                aqui.
              </p>
            )}
            {/* Sem id próprio: o cmdk grava o dele por cima, e o aria-controls do
                gatilho apontaria para o nada. O gatilho controla o popover. */}
            <CommandList className="max-h-72">
              <CommandEmpty>
                Nenhuma escola encontrada{busca.trim() ? ` para “${busca.trim()}”` : ''}. Confira a
                grafia ou busque pelo código EOL.
              </CommandEmpty>
              {visiveis.length > 0 && (
                <CommandGroup
                  heading={
                    busca.trim()
                      ? `${visiveis.length} de ${escolas.length} escola(s)`
                      : `${escolas.length} escola(s) nesta DRE`
                  }
                >
                  {visiveis.map((escola) => {
                    const posicao = escolhidas.indexOf(escola.codigoEol);
                    const escolhida = posicao >= 0;
                    const indisponivel = cheio && !escolhida;
                    return (
                      <CommandItem
                        key={escola.codigoEol}
                        value={escola.codigoEol}
                        disabled={indisponivel}
                        onSelect={() => alternar(escola.codigoEol)}
                        className="items-start gap-2.5 py-2"
                      >
                        <Check
                          aria-hidden="true"
                          className={cn(
                            'mt-0.5 h-4 w-4 shrink-0 text-primary',
                            escolhida ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-foreground">{escola.nome}</span>
                          <span className="block text-xs text-muted-foreground">
                            {detalheDaEscola(escola)}
                          </span>
                        </span>
                        {escolhida && (
                          <Badge variant="outline" className="shrink-0 border-primary/40">
                            {posicao + 1}ª opção
                          </Badge>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {escolhidas.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          Nenhuma escola escolhida ainda. A primeira que você escolher será a 1ª opção.
        </p>
      ) : (
        <ol
          ref={lista}
          aria-label="Escolas escolhidas, em ordem de preferência"
          className="divide-y divide-border rounded-xl border border-border"
        >
          {escolhidas.map((codigo, posicao) => {
            const escola = porCodigo.get(codigo);
            const nome = nomeDe(codigo);
            const primeira = posicao === 0;
            const ultima = posicao === escolhidas.length - 1;
            return (
              // No celular as ações descem para baixo do nome, que fica com a largura toda.
              <li
                key={codigo}
                data-escola={codigo}
                className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 p-3 text-sm sm:grid-cols-[auto_1fr_auto]"
              >
                <Badge variant="outline" className="shrink-0 border-primary/40 tabular-nums">
                  {posicao + 1}ª<span className="sr-only sm:not-sr-only sm:ml-1">opção</span>
                </Badge>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-foreground">{nome}</span>
                  {escola && (
                    <span className="block text-xs text-muted-foreground">
                      {detalheDaEscola(escola)}
                    </span>
                  )}
                </span>
                <span className="col-start-2 -ml-2 flex items-center gap-0.5 sm:col-start-auto sm:ml-0">
                  {primeira ? (
                    <span aria-hidden="true" className="h-9 w-9" />
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      data-acao="subir"
                      aria-label={`Subir ${nome} para ${posicao}ª opção`}
                      onClick={() => mover(codigo, -1)}
                    >
                      <ArrowUp aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  )}
                  {ultima ? (
                    <span aria-hidden="true" className="h-9 w-9" />
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      data-acao="descer"
                      aria-label={`Descer ${nome} para ${posicao + 2}ª opção`}
                      onClick={() => mover(codigo, 1)}
                    >
                      <ArrowDown aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    data-acao="remover"
                    aria-label={`Remover ${nome}`}
                    onClick={() => remover(codigo)}
                  >
                    <X aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </span>
              </li>
            );
          })}
        </ol>
      )}

      <p aria-live="polite" className="sr-only">
        {anuncio}
      </p>
    </div>
  );
}

export default SeletorDeEscolas;
